import { useEffect, useMemo, useState } from "react";
import { cn } from "@/cn";
import type { PortalsConfig } from "@career-ops-amplify/shared";
import {
  useDiscoveryProfile,
  useSaveDiscoveryProfile,
} from "@/hooks/useDiscovery";
import { useClientPagination } from "@/hooks/usePagination";
import { PaginationBar } from "@/components/pagination-bar";
import {
  CollectionCommandBar,
  useListViewMode,
  type EntitySortOption,
} from "@/components/collection";

const ATS_OPTIONS = [
  { value: "", label: "—" },
  { value: "greenhouse", label: "Greenhouse" },
  { value: "ashby", label: "Ashby" },
  { value: "lever", label: "Lever" },
  { value: "smartrecruiters", label: "SmartRecruiters" },
  { value: "rippling", label: "Rippling" },
  { value: "workable", label: "Workable" },
  { value: "workday", label: "Workday" },
  { value: "google", label: "Google (unsupported)" },
];

interface DiscoveryProfileEditorProps {
  compact?: boolean;
  onSaved?: () => void;
}

function csvToList(value: string): string[] {
  return value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function listToCsv(values?: string[]): string {
  return values?.join(", ") ?? "";
}

export function DiscoveryProfileEditor({
  compact = false,
  onSaved,
}: DiscoveryProfileEditorProps) {
  const { data: profile, isLoading } = useDiscoveryProfile();
  const saveMutation = useSaveDiscoveryProfile();

  const [companies, setCompanies] = useState<PortalsConfig["trackedCompanies"]>(
    [],
  );
  const [titlePositive, setTitlePositive] = useState("");
  const [titleNegative, setTitleNegative] = useState("");
  const [locationAllow, setLocationAllow] = useState("");
  const [locationBlock, setLocationBlock] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [sortOption, setSortOption] = useState<EntitySortOption>({
    field: "name",
    order: "asc",
    label: "Name A–Z",
  });

  const { viewMode, setViewMode, allowedModes } = useListViewMode({
    storageKey: "viewMode_portals_sources",
    defaultMode: "list",
    allowedModes: ["list", "table"],
  });

  useEffect(() => {
    if (!profile) return;
    setCompanies(profile.trackedCompanies ?? []);
    setTitlePositive(listToCsv(profile.titleFilter?.positive));
    setTitleNegative(listToCsv(profile.titleFilter?.negative));
    setLocationAllow(listToCsv(profile.locationFilter?.allow));
    setLocationBlock(listToCsv(profile.locationFilter?.block));
  }, [profile]);

  function addCompany() {
    setCompanies((prev) => [
      ...prev,
      { name: "", ats: "greenhouse", boardToken: "", enabled: true },
    ]);
  }

  function updateCompany(
    index: number,
    patch: Partial<PortalsConfig["trackedCompanies"][number]>,
  ) {
    setCompanies((prev) =>
      prev.map((c, i) => (i === index ? { ...c, ...patch } : c)),
    );
  }

  function removeCompany(index: number) {
    setCompanies((prev) => prev.filter((_, i) => i !== index));
  }

  const indexedCompanies = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = companies.map((company, index) => ({ company, index }));
    if (q) {
      list = list.filter(
        ({ company }) =>
          company.name.toLowerCase().includes(q) ||
          (company.ats ?? "").toLowerCase().includes(q) ||
          (company.boardToken ?? "").toLowerCase().includes(q),
      );
    }
    const dir = sortOption.order === "asc" ? 1 : -1;
    list.sort(
      (a, b) => a.company.name.localeCompare(b.company.name) * dir,
    );
    return list;
  }, [companies, search, sortOption]);

  const {
    pageItems: pagedCompanies,
    page,
    setPage,
    pageSize,
    setPageSize,
    totalPages,
    showingFrom,
    showingTo,
  } = useClientPagination(indexedCompanies, [search, sortOption.field, sortOption.order]);

  async function handleSave() {
    setMessage(null);
    try {
      await saveMutation.mutateAsync({
        trackedCompanies: companies.filter((c) => c.name.trim()),
        titleFilter: {
          positive: csvToList(titlePositive),
          negative: csvToList(titleNegative),
        },
        locationFilter: {
          allow: csvToList(locationAllow),
          block: csvToList(locationBlock),
        },
      });
      setMessage("Saved discovery profile.");
      onSaved?.();
    } catch (err) {
      setMessage(
        err instanceof Error ? err.message : "Failed to save profile.",
      );
    }
  }

  if (isLoading) {
    return (
      <div className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
        Loading discovery profile…
      </div>
    );
  }

  return (
    <div className={cn("space-y-5", compact ? "" : "rounded-lg border border-border bg-card p-5")}>
      <div>
        <h3 className="text-sm font-semibold text-foreground">
          My enabled employers
        </h3>
        <p className="mt-1 text-xs text-muted-foreground">
          ATS + board slug (not a paid key). Example: Greenhouse board{" "}
          <code className="text-[11px]">stripe</code> from boards.greenhouse.io/stripe.
          Prefer Enable scan on an employer profile when possible.
        </p>
      </div>

      {companies.length > 0 && (
        <CollectionCommandBar
          searchQuery={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search tracked companies…"
          sortOption={sortOption}
          onSortChange={setSortOption}
          sortOptions={[
            { field: "name", order: "asc", label: "Name A–Z" },
            { field: "name", order: "desc", label: "Name Z–A" },
          ]}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          allowedModes={allowedModes}
        />
      )}

      <div className="space-y-2">
        {companies.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No companies configured yet.
          </p>
        )}
        {companies.length > 0 && indexedCompanies.length === 0 && (
          <p className="text-sm text-muted-foreground">No companies match your search.</p>
        )}
        {viewMode === "table" && pagedCompanies.length > 0 ? (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/20">
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Name</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">ATS</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Token</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">On</th>
                  <th className="px-3 py-2 text-right font-medium text-muted-foreground"> </th>
                </tr>
              </thead>
              <tbody>
                {pagedCompanies.map(({ company, index }) => (
                  <tr key={index} className="border-b border-border last:border-0">
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        placeholder="Company name"
                        value={company.name}
                        onChange={(e) => updateCompany(index, { name: e.target.value })}
                        className="w-full rounded-md border border-border bg-card px-2 py-1.5 text-sm"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={company.ats ?? ""}
                        onChange={(e) => updateCompany(index, { ats: e.target.value })}
                        className="w-full rounded-md border border-border bg-card px-2 py-1.5 text-sm"
                      >
                        {ATS_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        placeholder="Board token"
                        value={company.boardToken ?? ""}
                        onChange={(e) =>
                          updateCompany(index, { boardToken: e.target.value })
                        }
                        className="w-full rounded-md border border-border bg-card px-2 py-1.5 text-sm"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() =>
                          updateCompany(index, { enabled: company.enabled === false })
                        }
                        className={cn(
                          "rounded-md border px-2 py-1.5 text-xs font-medium",
                          company.enabled !== false
                            ? "border-brand bg-brand/10 text-brand"
                            : "border-border text-muted-foreground",
                        )}
                      >
                        {company.enabled !== false ? "On" : "Off"}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => removeCompany(index)}
                        className="rounded-md border border-border px-2 py-1.5 text-xs text-muted-foreground hover:text-destructive"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          pagedCompanies.map(({ company, index }) => (
            <div
              key={index}
              className="grid gap-2 rounded-md border border-border bg-bg p-3 sm:grid-cols-[1fr_120px_120px_auto_auto]"
            >
              <input
                type="text"
                placeholder="Company name"
                value={company.name}
                onChange={(e) => updateCompany(index, { name: e.target.value })}
                className="rounded-md border border-border bg-card px-2 py-1.5 text-sm"
              />
              <select
                value={company.ats ?? ""}
                onChange={(e) => updateCompany(index, { ats: e.target.value })}
                className="rounded-md border border-border bg-card px-2 py-1.5 text-sm"
              >
                {ATS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Board token"
                value={company.boardToken ?? ""}
                onChange={(e) =>
                  updateCompany(index, { boardToken: e.target.value })
                }
                className="rounded-md border border-border bg-card px-2 py-1.5 text-sm"
              />
              <button
                type="button"
                onClick={() =>
                  updateCompany(index, { enabled: company.enabled === false })
                }
                className={cn(
                  "rounded-md border px-2 py-1.5 text-xs font-medium",
                  company.enabled !== false
                    ? "border-brand bg-brand/10 text-brand"
                    : "border-border text-muted-foreground",
                )}
              >
                {company.enabled !== false ? "On" : "Off"}
              </button>
              <button
                type="button"
                onClick={() => removeCompany(index)}
                className="rounded-md border border-border px-2 py-1.5 text-xs text-muted-foreground hover:text-destructive"
              >
                Remove
              </button>
            </div>
          ))
        )}
        {indexedCompanies.length > 0 && (
          <PaginationBar
            page={page}
            pageSize={pageSize}
            totalItems={indexedCompanies.length}
            totalPages={totalPages}
            showingFrom={showingFrom}
            showingTo={showingTo}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        )}
        <button
          type="button"
          onClick={addCompany}
          className="text-xs font-medium text-brand hover:underline"
        >
          + Add company
        </button>
      </div>

      {!compact && (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium">Default title keywords</label>
            <input
              type="text"
              value={titlePositive}
              onChange={(e) => setTitlePositive(e.target.value)}
              placeholder="engineer, senior, staff"
              className="rounded-md border border-border bg-bg px-3 py-2 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium">Exclude titles</label>
            <input
              type="text"
              value={titleNegative}
              onChange={(e) => setTitleNegative(e.target.value)}
              placeholder="junior, intern"
              className="rounded-md border border-border bg-bg px-3 py-2 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium">Allowed locations</label>
            <input
              type="text"
              value={locationAllow}
              onChange={(e) => setLocationAllow(e.target.value)}
              placeholder="remote, san francisco"
              className="rounded-md border border-border bg-bg px-3 py-2 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium">Blocked locations</label>
            <input
              type="text"
              value={locationBlock}
              onChange={(e) => setLocationBlock(e.target.value)}
              placeholder="india, china"
              className="rounded-md border border-border bg-bg px-3 py-2 text-sm"
            />
          </div>
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={handleSave}
          disabled={saveMutation.isPending}
          className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground disabled:opacity-50"
        >
          {saveMutation.isPending ? "Saving…" : "Save profile"}
        </button>
        {message && (
          <span className="text-xs text-muted-foreground">{message}</span>
        )}
      </div>
    </div>
  );
}
